<template>
  <div>
    <div class="row">
      <div
        v-if="costRegistrationLoaded"
        class="col-md-12 mr-auto ml-auto"
      >
        <card :title="$t('sidebar.costReport')">
          <!-- Table Select Tabs -->
          <template #table-select>
            <NavPills
              :model-value="safeActiveIndex"
              :items="navPillItems"
              @update:model-value="initTable"
            />
          </template>

          <!-- Search Input -->
          <div v-if="!showProjectDetails" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mb-3">
            <div class="col-12 col-md-4 col-sm-6">
              <el-input
                v-model="searchQuery"
                class="search-input"
                clearable
                :prefix-icon="Search"
                :placeholder="$t('common.searchRecords')"
                aria-controls="datatables"
                @keyup="search"
                @clear="handleClearSearch"
              />
            </div>
          </div>

          <!-- Date Range Picker -->
          <div class="calendar d-flex align-items-center mb-3">
            <div class="d-flex align-items-center" style="flex: 1;">
              <el-date-picker
                v-model="fromDate"
                class="flex-grow-1 form-group"
                type="date"
                :placeholder="$t('common.from')"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                @change="fetchCostRegistration"
              />
            </div>
            <span class="mx-2">-</span>
            <div class="d-flex align-items-center" style="flex: 1;">
              <el-date-picker
                v-model="toDate"
                class="flex-grow-1 form-group"
                type="date"
                :placeholder="$t('common.to')"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                @change="fetchCostRegistration"
              />
            </div>
          </div>

          <!-- Project Groups List View -->
          <template v-if="!showProjectDetails">
            <!-- Desktop Table -->
            <div v-if="!isMobile">
              <el-table :data="groupedReservations.projectGroups" @row-click="expandProject">
                <el-table-column prop="projectName" :label="$t('common.project')" min-width="150" />
                <el-table-column prop="totalAmount" :label="$t('common.totalAmount')" min-width="50">
                  <template #default="{ row }">{{ formatNumber(row.totalAmount) }}</template>
                </el-table-column>
                <el-table-column prop="totalAmountWithVAT" :label="$t('common.totalAmountVAT')" min-width="50">
                  <template #default="{ row }">{{ formatNumber(row.totalAmountWithVAT) }}</template>
                </el-table-column>
              </el-table>
              <el-table v-if="groupedReservationsCalculated" :data="[groupedReservations]">
                <el-table-column :label="$t('common.totalAmount')" min-width="150" />
                <el-table-column :label="formatNumber(groupedReservations.totalAmount)" min-width="50" />
                <el-table-column :label="formatNumber(groupedReservations.totalAmountWithVAT)" min-width="50" />
              </el-table>
            </div>
            <!-- Mobile Table -->
            <div v-else>
              <el-table :data="groupedReservations.projectGroups" @row-click="expandProject">
                <el-table-column :label="$t('common.info')" min-width="150">
                  <template #default="{ row }">
                    <div>
                      <div><strong>{{ $t('common.project') }}:</strong> {{ row.projectName }}</div>
                      <div><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(row.totalAmount) }}</div>
                      <div><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(row.totalAmountWithVAT) }}</div>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
              <div class="mt-3">
                <div><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(groupedReservations.totalAmount) }}</div>
                <div><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(groupedReservations.totalAmountWithVAT) }}</div>
              </div>
            </div>
          </template>

          <!-- Project Details View -->
          <template v-if="showProjectDetails">
            <div class="project-details-section">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h5 class="mb-0 font-weight-bolder">{{ selectedProject.projectName }}</h5>
                <span v-if="safeActiveIndex === 0" class="project-close-btn" @click="collapseProject">
                  <i class="material-icons">close</i>
                </span>
              </div>
              <div class="project-header mb-3">
                <h5>{{ $t('common.totalAmount') }}: {{ formatNumber(selectedProject.totalAmount) }} kr</h5>
                <h5>{{ $t('common.totalAmountVAT') }}: {{ formatNumber(selectedProject.totalAmountWithVAT) }} kr</h5>
              </div>

              <div v-if="erpSyncResult" class="alert alert-dismissible p-2 pe-5 text-sm mb-3" :class="erpSyncResult.failures.length || erpSyncResult.errorMessage ? 'alert-warning' : 'alert-success'">
                <button type="button" class="btn-close p-2" style="font-size:0.7rem" @click="erpSyncResult = null"></button>
                <div v-if="erpSyncResult.errorMessage" class="fw-semibold">{{ erpSyncResult.errorMessage }}</div>
                <div>{{ $t('timeRegistration.syncToErpResult', { created: erpSyncResult.created, updated: erpSyncResult.updated, failed: erpSyncResult.failures.length }) }}</div>
                <ul v-if="erpSyncResult.failures.length" class="mb-0 mt-1">
                  <li v-for="(f, fi) in erpSyncResult.failures" :key="fi">{{ f.label }} — {{ f.details }}</li>
                </ul>
              </div>

              <!-- Desktop Reservations Table -->
              <el-table v-if="!isMobile" :data="selectedProject.reservations">
                <el-table-column :label="$t('tools.tools')">
                  <template #default="{ row }">
                    <div>#{{ row.tool.customerToolId }} {{ row.tool.name }}</div>
                    <div v-if="enableERPCostSync" class="text-xs" :class="row.erpSyncedAt ? 'text-success' : 'text-secondary'">
                      <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">{{ row.erpSyncedAt ? 'cloud_done' : 'cloud_off' }}</i>
                      {{ row.erpSyncedAt ? $t('timeRegistration.syncedToErp', { erp: prettyERPName || 'ERP' }) + ' ' + formatSyncTimestamp(row.erpSyncedAt) : $t('timeRegistration.notSyncedToErp') }}
                    </div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('tools.reservation')">
                  <template #default="{ row }">
                    <div>{{ setupDateRange(row.reservedTimestamp, row.unreservedTimestamp, row.dateRange) }}</div>
                    <div v-if="row.rented">{{ $t('tools.rented') }}: {{ row.externalUser }}</div>
                  </template>
                </el-table-column>
                <el-table-column v-if="enableProjectPhases" :min-width="80" :label="$t('projects.phase')">
                  <template #default="{ row }">{{ row.projectPhaseName || '' }}</template>
                </el-table-column>
                <el-table-column v-if="!enableToolPriceTypes" :min-width="60" prop="dailyPrice" :label="$t('tools.dailyPrice')">
                  <template #default="{ row }">
                    <div>{{ formatNumber(row.dailyPrice) }}</div>
                    <div v-if="row.quantity !== null">{{ $t('tools.quantity') }}: {{ row.quantity }}</div>
                  </template>
                </el-table-column>
                <el-table-column v-else :min-width="60" prop="dailyPrice" :label="$t('tools.cost')">
                  <template #default="{ row }">
                    <div>{{ formatNumber(row.dailyPrice) }}</div>
                    <div v-if="row.quantity !== null">{{ $t('analytics.totalTools') }}: {{ row.quantity }}</div>
                    <div>{{ $t('tools.unit') }}: {{ formatToolPriceType(row.toolPriceTypeId) }}</div>
                    <div v-if="row.units !== null">{{ $t('tools.unitCountAbbreviation') }}: {{ row.units }}</div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('common.amount')">
                  <template #default="{ row }">
                    <div v-if="row.unreservedTimestamp === null" class="text-info">{{ formatNumber(row.totalPrice) }} ({{ $t('tools.reserved') }})</div>
                    <div v-else-if="row.parentReservationId" class="text-warning">{{ formatNumber(row.totalPrice) }} ({{ $t('common.subReservation') }})</div>
                    <div v-else-if="!row.totalPriceOverridden">{{ formatNumber(row.totalPrice) }}</div>
                    <div v-else class="text-danger">{{ formatNumber(row.totalPrice) }} ({{ $t('common.updated') }})</div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('common.amountVAT')">
                  <template #default="{ row }">
                    <div v-if="row.unreservedTimestamp === null" class="text-info">{{ formatNumber(row.totalPriceWithVAT) }} ({{ $t('tools.reserved') }})</div>
                    <div v-else-if="row.parentReservationId" class="text-warning">{{ formatNumber(row.totalPriceWithVAT) }} ({{ $t('common.subReservation') }})</div>
                    <div v-else-if="!row.totalPriceOverridden">{{ formatNumber(row.totalPriceWithVAT) }}</div>
                    <div v-else class="text-danger">{{ formatNumber(row.totalPriceWithVAT) }} ({{ $t('common.updated') }})</div>
                  </template>
                </el-table-column>
                <el-table-column :min-width="40" align="right" :label="$t('common.actions')">
                  <template #default="{ row, $index }">
                    <div class="actions-container">
                      <material-checkbox v-if="safeActiveIndex === 0 && showProjectDetails && pickToolsInProjectCostReport" :id="'project-checkbox-' + $index" @click.stop="projectReservationsToPrint($event, row)" />
                      <material-checkbox v-if="safeActiveIndex !== 0" :id="'external-checkbox-' + $index" @click.stop="externalReservationsToPrint($event, row)" />
                      <el-button type="warning" circle size="small" @click.stop="handleReservationEdit($index, row)">
                        <el-icon><Edit /></el-icon>
                      </el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>

              <!-- Mobile Reservations Table -->
              <el-table v-else :data="selectedProject.reservations">
                <el-table-column :label="$t('common.info')" min-width="150">
                  <template #default="{ row, $index }">
                    <div>
                      <div><strong>{{ $t('tools.tools') }}:</strong> #{{ row.tool.customerToolId }} {{ row.tool.name }}</div>
                      <div v-if="enableERPCostSync" class="text-xs" :class="row.erpSyncedAt ? 'text-success' : 'text-secondary'">
                        <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">{{ row.erpSyncedAt ? 'cloud_done' : 'cloud_off' }}</i>
                        {{ row.erpSyncedAt ? $t('timeRegistration.syncedToErp', { erp: prettyERPName || 'ERP' }) + ' ' + formatSyncTimestamp(row.erpSyncedAt) : $t('timeRegistration.notSyncedToErp') }}
                      </div>
                      <div v-if="row.quantity !== null"><strong>{{ $t('analytics.totalTools') }}:</strong> {{ row.quantity }}</div>
                      <div><strong>{{ $t('tools.reservation') }}:</strong> {{ setupDateRange(row.reservedTimestamp, row.unreservedTimestamp, row.dateRange) }}</div>
                      <div v-if="enableProjectPhases && row.projectPhaseName"><strong>{{ $t('projects.phase') }}:</strong> {{ row.projectPhaseName }}</div>
                      <div v-if="row.rented"><strong>{{ $t('tools.rentedTo') }}:</strong> {{ row.externalUser }}</div>
                      <div v-if="!enableToolPriceTypes"><strong>{{ $t('tools.dailyPrice') }}:</strong> {{ formatNumber(row.dailyPrice) }}</div>
                      <div v-else><strong>{{ $t('tools.cost') }}:</strong> {{ formatNumber(row.dailyPrice) }}</div>
                      <div v-if="enableToolPriceTypes"><strong>{{ $t('tools.unit') }}:</strong> {{ formatToolPriceType(row.toolPriceTypeId) }}</div>
                      <div v-if="row.units !== null"><strong>{{ $t('tools.unitCountAbbreviation') }}:</strong> {{ row.units }}</div>
                      <template v-if="row.unreservedTimestamp === null">
                        <div class="text-info"><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(row.totalPrice) }}*</div>
                        <div class="text-info"><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(row.totalPriceWithVAT) }}*</div>
                        <div class="text-info"><strong>{{ $t('tools.reserved') }}:</strong> {{ $t('common.yes') }}</div>
                      </template>
                      <template v-else-if="row.parentReservationId">
                        <div class="text-warning"><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(row.totalPrice) }}*</div>
                        <div class="text-warning"><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(row.totalPriceWithVAT) }}*</div>
                        <div class="text-warning"><strong>{{ $t('common.subReservation') }}:</strong> {{ $t('common.yes') }}</div>
                      </template>
                      <template v-else-if="!row.totalPriceOverridden">
                        <div><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(row.totalPrice) }}</div>
                        <div><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(row.totalPriceWithVAT) }}</div>
                      </template>
                      <template v-else>
                        <div class="text-danger"><strong>{{ $t('common.totalAmount') }}:</strong> {{ formatNumber(row.totalPrice) }}*</div>
                        <div class="text-danger"><strong>{{ $t('common.totalAmountVAT') }}:</strong> {{ formatNumber(row.totalPriceWithVAT) }}*</div>
                        <div class="text-danger"><strong>{{ $t('common.updated') }}:</strong> {{ $t('common.yes') }}</div>
                      </template>
                      <div class="action-container mt-2">
                        <material-checkbox v-if="safeActiveIndex === 0 && showProjectDetails && pickToolsInProjectCostReport" :id="'project-checkbox-mobile-' + $index" @click.stop="projectReservationsToPrint($event, row)" />
                        <material-checkbox v-if="safeActiveIndex !== 0" :id="'external-checkbox-mobile-' + $index" @click.stop="externalReservationsToPrint($event, row)" />
                        <el-button type="warning" circle size="small" @click.stop="handleReservationEdit($index, row)">
                          <el-icon><Edit /></el-icon>
                        </el-button>
                      </div>
                    </div>
                  </template>
                </el-table-column>
              </el-table>

              <!-- Download / Sync Buttons: equal halves of the row (download alone spans it) -->
              <div class="d-flex gap-3 mt-3">
                <material-button class="flex-fill" color="dark" @click="generatePDF(selectedProject)">
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">download</i>{{ $t('common.downloadPDF') }}
                </material-button>
                <material-button v-if="allowCostSyncToERP" class="flex-fill" color="dark" :disabled="costSyncWithERPInProgress" @click="syncToERP(selectedProject)">
                  <span v-if="costSyncWithERPInProgress" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="material-icons me-1" style="font-size:1rem;vertical-align:middle">sync</i>
                  {{ $t('common.syncTo') + prettyERPName }}
                </material-button>
              </div>
            </div>
          </template>

          <!-- Update Reservation Modal -->
          <modal
            :show="showUpdateReservationDaysModal"
            class="white-content"
            @update:show="showUpdateReservationDaysModal = $event"
          >
            <h3 class="card-title">{{ $t('tools.updateReservation') }}</h3>
            <div class="row">
              <div v-if="unitsForUpdate === null" class="col-12"><label>{{ $t('common.changeDates') }}</label></div>
            </div>
            <div class="row">
              <div class="col-12">
                <div v-if="unitsForUpdate === null" class="calendar d-flex align-items-center">
                  <div class="d-flex align-items-center" style="flex: 1;">
                    <el-date-picker v-model="updatedReservationDate" class="flex-grow-1 form-group" type="date" :placeholder="$t('common.from')" format="DD/MM/YYYY" value-format="YYYY-MM-DD" @change="updateTotalPrice" />
                  </div>
                  <span class="mx-2">-</span>
                  <div class="d-flex align-items-center" style="flex: 1;">
                    <el-date-picker v-model="updatedUnreservationDate" class="flex-grow-1 form-group" type="date" :placeholder="$t('common.to')" format="DD/MM/YYYY" value-format="YYYY-MM-DD" @change="updateTotalPrice" />
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <material-checkbox id="updatePrice" v-model="updatePrice">{{ $t('common.updateTotalAmountVAT') }}</material-checkbox>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <material-input id="updatedTotalPrice" v-model:value="updatedTotalPriceStr" :disabled="!updatePrice" :placeholder="$t('common.totalAmountVAT')" />
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-sm d-flex justify-content-end">
                <material-button color="dark" @click="handleReservationUpdate">{{ $t('common.submit') }}</material-button>
              </div>
            </div>
          </modal>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import { ElTable, ElTableColumn, ElDatePicker, ElInput, ElButton, ElIcon } from 'element-plus';
import { Search, Edit } from '@element-plus/icons-vue';
import api from "../../services/api";
import { loadCustomerMeCached } from '../../services/customerMeHelper';
import { getApiErrorMessage } from "../../utils/apiError";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import swal from 'sweetalert2';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Cookies from 'js-cookie';
import { jwtDecode } from "jwt-decode";
import Card from "../../components/Card.vue";
import Modal from "../../components/Modals/Modal.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialCheckbox from "../../components/MaterialCheckbox.vue";
import MaterialInput from "../../components/MaterialInput.vue";
import NavPills from "../../components/NavPills.vue";

export default {
  name: 'CostReport',
  components: {
    ElTable, ElTableColumn, ElDatePicker, ElInput, ElButton, ElIcon,
    Card, Modal, MaterialButton, MaterialCheckbox, MaterialInput, Edit, NavPills
  },
  data() {
    return {
      Search,
      customerId: '',
      toDate: null,
      fromDate: null,
      originalReservationDate: null,
      originalUnreservationDate: null,
      updatedReservationDate: null,
      updatedUnreservationDate: null,
      isSubReservation: false,
      parentReservationId: null,
      updatedTotalPrice: null,
      dailyPriceForUpdate: null,
      quantityForUpdate: null,
      unitsForUpdate: null,
      reservationData: [],
      selectedProjectId: null,
      customerInformation: {},
      costRegistrationLoaded: false,
      searchQuery: '',
      isMobile: window.innerWidth <= 1024,
      showProjectDetails: false,
      showUpdateReservationDaysModal: false,
      latestUnreservedId: '',
      latestUnreservedEntity: 'ToolReservation',
      expandedProject: null,
      updatePrice: false,
      erpName: '',
      costSyncWithERPInProgress: false,
      erpSyncResult: null,
      erpSyncLineLabels: {},
      enableToolPriceTypes: false,
      enableProjectPhases: false,
      activeIndex: 0,
      indexChange: false,
      externalReservationIds: [],
      enableERPCostSync: false,
      forceProjectConnectionOnRentals: false,
      projectToolIdsToPrint: [],
      pickToolsInProjectCostReport: false,
      createSubReservation: false
    };
  },
  computed: {
    updatedTotalPriceStr: {
      get() { return this.updatedTotalPrice !== null ? String(this.updatedTotalPrice) : ''; },
      set(val) { this.updatedTotalPrice = val === '' ? null : Number(val); }
    },
    groupedReservationsCalculated() {
      return this.reservationData && 
             this.reservationData.projectGroups && 
             this.reservationData.projectGroups.length > 0;
    },
    toolPriceTypeOptions() {
      return [
        { id: 1, type: this.$t('toolPriceType.day') },
        { id: 2, type: this.$t('toolPriceType.hour') },
        { id: 3, type: this.$t('toolPriceType.kilometer') },
        { id: 4, type: this.$t('toolPriceType.meter') }
      ];
    },
    tableCategories() {
      const cats = [{ name: this.$t('common.reservation'), icon: 'receipt' }];
      if (!this.forceProjectConnectionOnRentals) cats.push({ name: this.$t('tools.rented'), icon: 'assignment_return' });
      return cats;
    },
    safeActiveIndex: {
      get() { return this.tableCategories.length === 1 ? 0 : this.activeIndex; },
      set(val) { this.activeIndex = val; }
    },
    navPillItems() {
      return this.tableCategories.map(c => ({ label: c.name, icon: c.icon }));
    },
    allowCostSyncToERP() {
      return (this.erpName === "DK" || this.erpName === "DKWithProjectPhases" || this.erpName === "BusinessCentralCustomers" || this.erpName === "BusinessCentralEndCustomers") && this.enableERPCostSync && this.safeActiveIndex === 0;
    },
    prettyERPName() {
      switch (this.erpName) {
        case "DK": case "DKWithProjectPhases": return "DK";
        case "BusinessCentralCustomers": case "BusinessCentralEndCustomers": return "Business Central";
        default: return "";
      }
    },
    groupedReservations() {
      const selectedStart = new Date(this.fromDate);
      const selectedEnd = new Date(this.toDate);
      const grouped = { ...this.reservationData };
      if (!grouped.projectGroups) return { projectGroups: [], totalAmount: 0, totalAmountWithVAT: 0 };
      
      const rentProjectIndex = grouped.projectGroups.findIndex(g => g.projectName === "RentProject");
      if (rentProjectIndex !== -1) {
        if (this.safeActiveIndex === 0) grouped.projectGroups.splice(rentProjectIndex, 1);
        else grouped.projectGroups[rentProjectIndex].projectName = this.$t("tools.rented");
      }

      const dateOnly = (date) => new Date(date.split('T')[0]);
      const formatDate = (date) => {
        const d = new Date(date);
        return `${('0' + d.getDate()).slice(-2)}/${('0' + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
      };
      const addDays = (date, days) => { const d = new Date(date); d.setDate(d.getDate() + days); return d; };
      const subtractDays = (date, days = 1) => { const d = new Date(date); d.setDate(d.getDate() - days); return d; };

      const getMergedIntervals = (subs, effectiveStart, effectiveEnd) => {
        let intervals = [];
        subs.forEach(sub => {
          let start = dateOnly(sub.reservedTimestamp);
          if (start < effectiveStart) start = new Date(effectiveStart);
          let end = sub.unreservedTimestamp ? dateOnly(sub.unreservedTimestamp) : new Date(effectiveEnd);
          if (end > effectiveEnd) end = new Date(effectiveEnd);
          if (start <= end) intervals.push({ start, end });
        });
        if (intervals.length === 0) return [];
        intervals.sort((a, b) => a.start - b.start);
        let merged = [intervals[0]];
        for (let i = 1; i < intervals.length; i++) {
          let current = merged[merged.length - 1];
          let next = intervals[i];
          if (next.start <= addDays(current.end, 1)) current.end = new Date(Math.max(current.end, next.end));
          else merged.push(next);
        }
        return merged;
      };

      const computeDateGaps = (effectiveStart, effectiveEnd, subs) => {
        const merged = getMergedIntervals(subs, effectiveStart, effectiveEnd);
        let intervals = [];
        let pointer = new Date(effectiveStart);
        merged.forEach(interval => {
          if (pointer < interval.start) {
            const gapEnd = subtractDays(interval.start, 1);
            if (pointer <= gapEnd) intervals.push(`${formatDate(pointer)} - ${formatDate(gapEnd)}`);
          }
          pointer = addDays(interval.end, 1);
        });
        if (pointer <= effectiveEnd) intervals.push(`${formatDate(pointer)} - ${formatDate(effectiveEnd)}`);
        return intervals;
      };

      const computeRemainingDays = (effectiveStart, effectiveEnd, subs) => {
        const merged = getMergedIntervals(subs, effectiveStart, effectiveEnd);
        let coveredDays = 0;
        merged.forEach(int => { coveredDays += Math.round((int.end - int.start) / (1000 * 60 * 60 * 24)) + 1; });
        const effectiveDays = Math.round((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(0, effectiveDays - coveredDays);
      };

      const processGroup = (group) => {
        const parents = group.reservations.filter(r => !r.parentReservationId);
        let adjustedReservations = [];

        parents.forEach(parent => {
          const parentDailyPrice = Number(parent.dailyPrice);
          const subs = parent.rented
            ? group.reservations.filter(r => r.parentReservationId === parent.id && r.rented)
            : group.reservations.filter(r => r.parentReservationId === parent.id && !r.rented);

          let effectiveStart = dateOnly(parent.reservedTimestamp);
          if (effectiveStart < selectedStart) effectiveStart = new Date(selectedStart);
          let effectiveEnd = parent.unreservedTimestamp ? dateOnly(parent.unreservedTimestamp) : new Date(selectedEnd);
          if (effectiveEnd > selectedEnd) effectiveEnd = new Date(selectedEnd);

          const remainingDays = computeRemainingDays(effectiveStart, effectiveEnd, subs);
          const gaps = computeDateGaps(effectiveStart, effectiveEnd, subs);
          const dateGapStr = gaps.join(", ") || (subs.length === 0 ? `${formatDate(effectiveStart)} - ${formatDate(effectiveEnd)}` : "");

          const adjustedParent = { ...parent };
          adjustedParent.totalPriceWithVAT = parentDailyPrice * remainingDays;
          adjustedParent.totalPrice = adjustedParent.totalPriceWithVAT / 1.24;
          adjustedParent.dateRange = dateGapStr;

          if (remainingDays <= 0) {
            adjustedParent.totalPriceWithVAT = 0;
            adjustedParent.totalPrice = 0;
            adjustedParent.covered = true;
          }
          adjustedReservations.push(adjustedParent);
          subs.sort((a, b) => a.reservedTimestamp >= b.reservedTimestamp ? 1 : -1);
          subs.forEach(sub => adjustedReservations.push(sub));
        });

        const newTotalAmount = adjustedReservations.reduce((sum, r) => sum + Number(r.totalPrice || 0), 0);
        const newTotalAmountWithVAT = adjustedReservations.reduce((sum, r) => sum + Number(r.totalPriceWithVAT || 0), 0);

        const sortedFinalReservations = [];
        const sortedParents = adjustedReservations.filter(r => !r.parentReservationId).sort((a, b) => {
          const aTime = a.unreservedTimestamp ? new Date(a.unreservedTimestamp).getTime() : Number.POSITIVE_INFINITY;
          const bTime = b.unreservedTimestamp ? new Date(b.unreservedTimestamp).getTime() : Number.POSITIVE_INFINITY;
          if (aTime !== bTime) return bTime - aTime;
          return a.id - b.id;
        });
        sortedParents.forEach(parent => {
          sortedFinalReservations.push(parent);
          const children = adjustedReservations.filter(r => r.parentReservationId === parent.id).sort((a, b) => new Date(a.reservedTimestamp) - new Date(b.reservedTimestamp));
          sortedFinalReservations.push(...children);
        });

        return { ...group, reservations: sortedFinalReservations, totalAmount: newTotalAmount, totalAmountWithVAT: newTotalAmountWithVAT };
      };

      let processedGroups = grouped.projectGroups.map(group => processGroup(group));
      processedGroups = processedGroups.sort((a, b) => {
        if (a.projectName === this.$t("tools.rented")) return 1;
        if (b.projectName === this.$t("tools.rented")) return -1;
        return a.projectName.localeCompare(b.projectName);
      });

      if (this.searchQuery) {
        processedGroups = processedGroups.filter(group => group.projectName && group.projectName.toLowerCase().includes(this.searchQuery.toLowerCase()));
      }

      const totalAmount = processedGroups.reduce((sum, group) => sum + group.totalAmount, 0);
      const totalAmountWithVAT = processedGroups.reduce((sum, group) => sum + group.totalAmountWithVAT, 0);

      return { 
        ...grouped, 
        projectGroups: processedGroups,
        totalAmount,
        totalAmountWithVAT
      };
    },
    selectedProject() {
      return this.groupedReservations.projectGroups.find(g => g.projectId === this.selectedProjectId) ?? {
        projectId: this.selectedProjectId,
        projectKey: this.selectedProjectId,
        projectName: this.$t("tools.rented"),
        reservations: [],
        totalAmount: 0,
        totalAmountWithVAT: 0
      };
    }
  },
  async created() {
    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.customerId = decodedToken.CustomerId;
    window.addEventListener('resize', this.updateIsMobile);

    try {
      const config = await getCustomerConfig('EnableToolPriceTypes');
      this.enableToolPriceTypes = config === '' ? false : config;
    } catch (e) { this.enableToolPriceTypes = false; }

    try {
      const config = await getCustomerConfig('EnableProjectPhases');
      this.enableProjectPhases = config === true;
    } catch (e) { this.enableProjectPhases = false; }

    try {
      const config = await getCustomerConfig('PickToolsInProjectCostReport');
      this.pickToolsInProjectCostReport = config === '' ? false : config;
    } catch (e) { this.pickToolsInProjectCostReport = false; }

    // erpName comes from the cached /Customers/Me helper — a cookie copy here used to go
    // permanently stale (e.g. set to '' after one failed fetch), silently hiding the sync button
    try {
      const customer = await loadCustomerMeCached();
      this.erpName = customer?.erpName || '';
    } catch (error) { console.error('API request error:', error); } // eslint-disable-line no-console

    try {
      const config = await getCustomerConfig('EnableERPCostSync');
      this.enableERPCostSync = config === '' ? false : config;
    } catch (e) { this.enableERPCostSync = false; }

    try {
      const config = await getCustomerConfig('ForceProjectConnectionOnRentals');
      this.forceProjectConnectionOnRentals = config === '' ? false : config;
    } catch (e) { this.forceProjectConnectionOnRentals = false; }

    this.initializeDateRange();
    this.fetchCustomerInformation();
    this.initTable(0);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() { this.isMobile = window.innerWidth <= 1024; },
    search() {
      // Filtering is handled reactively by groupedReservations computed
    },
    handleClearSearch() {
      // Filtering is handled reactively by groupedReservations computed
    },
    initializeDateRange() {
      const today = new Date();
      this.toDate = this.formatDateValue(today);
      this.fromDate = this.formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1));
    },
    formatDateValue(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '';
      const parts = Math.round(num).toFixed(2).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    },
    formatToolPriceType(id) {
      const opt = this.toolPriceTypeOptions.find(o => o.id === id);
      return opt ? opt.type : '';
    },
    formatDateDisplay(date) {
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    },
    setupDateRange(resTimestamp, unresTimestamp, dateRange) {
      if (dateRange) return dateRange;
      let display = resTimestamp < this.fromDate ? this.formatDateDisplay(this.fromDate) : this.formatDateDisplay(resTimestamp);
      display += unresTimestamp <= this.toDate ? ` - ${this.formatDateDisplay(unresTimestamp)}` : ` - ${this.formatDateDisplay(this.toDate)}`;
      return display;
    },
    initTable(index) {
      this.activeIndex = Number.isInteger(index) ? index : this.activeIndex;
      this.indexChange = true;
      this.fetchCostRegistration();
    },
    async fetchCostRegistration() {
      const url = this.safeActiveIndex === 0
        ? `/CostReport/Reservations?fromDate=${this.fromDate}&toDate=${this.toDate}&includeReservations=true&includeExternalReservations=true`
        : `/CostReport/Reservations?fromDate=${this.fromDate}&toDate=${this.toDate}&includeReservations=false&includeExternalReservations=true`;
      try {
        const response = await api.get(url);
        this.reservationData = response.data;
        if (this.indexChange) {
          if (this.safeActiveIndex === 0) {
            this.externalReservationIds = [];
            this.selectedProjectId = null;
            this.showProjectDetails = false;
          } else {
            this.selectedProjectId = '00000000-0000-0000-0000-000000000000';
            this.showProjectDetails = true;
          }
          this.indexChange = false;
        }
        this.costRegistrationLoaded = true;
      } catch (error) { console.error('API request error:', error); } // eslint-disable-line no-console
    },
    async fetchCustomerInformation() {
      try {
        this.customerInformation = await loadCustomerMeCached();
      } catch (error) { console.error('API request error:', error); } // eslint-disable-line no-console
    },
    expandProject(row) {
      this.erpSyncResult = null;
      this.selectedProjectId = row.projectId;
      this.expandedProject = row;
      this.showProjectDetails = true;
      this.indexChange = false;
      window.scrollTo(0, 0);
    },
    collapseProject() {
      this.erpSyncResult = null;
      this.fetchCostRegistration();
      this.expandedProject = null;
      this.selectedProjectId = null;
      this.showProjectDetails = false;
      this.projectToolIdsToPrint = [];
    },
    externalReservationsToPrint(event, row) {
      const input = event.target.tagName === 'INPUT' ? event.target : event.currentTarget.querySelector('input[type="checkbox"]');
      if (!input) return;
      const isChecked = input.checked;
      if (isChecked) { if (!this.externalReservationIds.includes(row.id)) this.externalReservationIds.push(row.id); }
      else this.externalReservationIds = this.externalReservationIds.filter(id => id !== row.id);
    },
    projectReservationsToPrint(event, row) {
      const input = event.target.tagName === 'INPUT' ? event.target : event.currentTarget.querySelector('input[type="checkbox"]');
      if (!input) return;
      const isChecked = input.checked;
      if (isChecked) { if (!this.projectToolIdsToPrint.includes(row.id)) this.projectToolIdsToPrint.push(row.id); }
      else this.projectToolIdsToPrint = this.projectToolIdsToPrint.filter(id => id !== row.id);
    },
    handleReservationEdit(index, row) {
      const emptyPlaceholder = 'X';
      let html = "", title = "", confirmButtonText = "";
      if (row.unreservedTimestamp !== null) {
        title = row.parentReservationId ? this.$t('common.subReservation') : this.$t('common.reservation');
        html = `${this.$t('tools.toolReserved')} ${new Date(row.reservedTimestamp).toLocaleDateString('is-IS')} - ${new Date(row.unreservedTimestamp).toLocaleDateString('is-IS')} <br/> ${this.$t('common.totalAmount')} ${this.formatNumber(row.totalPriceWithVAT)} Kr.`;
        confirmButtonText = this.$t('tools.updateReservation');
      } else {
        if (row.toolPriceTypeId !== 1) {
					this.$toast.error(this.$t('errors.subReservationForUnits'))
          return;
        }
        title = this.$t('common.reservation');
        html = `${this.$t('tools.toolReserved')} ${new Date(row.reservedTimestamp).toLocaleDateString('is-IS')} - ${emptyPlaceholder} <br/> ${this.$t('common.totalAmount')} ${this.formatNumber(row.totalPriceWithVAT)} Kr.`;
        confirmButtonText = this.$t('tools.createSubReservation');
      }
      swal.fire({
        title, html, icon: 'success', showCancelButton: true,
        customClass: { confirmButton: 'btn btn-danger btn-fill', cancelButton: 'btn btn-success btn-fill' },
        confirmButtonText, cancelButtonText: this.$t('delete.cancel'), buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.latestUnreservedId = row.id;
          this.latestUnreservedEntity = row.rented ? 'ExternalReservation' : 'ToolReservation';
          this.createSubReservation = !row.unreservedTimestamp;
          this.isSubReservation = !!row.parentReservationId;
          this.parentReservationId = row.parentReservationId;
          if (this.createSubReservation) {
            this.updatedReservationDate = new Date(row.reservedTimestamp.split('T')[0]) < new Date(this.fromDate) ? this.fromDate : this.formatDateValue(row.reservedTimestamp);
            this.originalReservationDate = this.updatedReservationDate;
            this.originalUnreservationDate = row.unreservedTimestamp ? this.formatDateValue(row.unreservedTimestamp) : this.toDate;
          } else this.updatedReservationDate = this.formatDateValue(row.reservedTimestamp);
          this.updatedUnreservationDate = row.unreservedTimestamp ? this.formatDateValue(row.unreservedTimestamp) : this.toDate;
          this.updatedTotalPrice = row.totalPriceWithVAT;
          this.dailyPriceForUpdate = row.dailyPrice;
          this.quantityForUpdate = row.quantity ?? 1;
          this.unitsForUpdate = row.units;
          this.showUpdateReservationDaysModal = true;
        }
      });
    },
    handleReservationUpdate() {
      if (!this.isValid()) return;
      this.createSubReservation ? this.subReservationCreate() : this.reservationEdit();
    },
    async reservationEdit() {
      let url = `/Tool/Reservation/${this.latestUnreservedId}`;
      if (this.latestUnreservedEntity === 'ExternalReservation') url = `/Tool/Reservation/External/${this.latestUnreservedId}`;
      const requestBody = { updatedReservedTimestamp: this.updatedReservationDate, updatedUnreservedTimestamp: this.updatedUnreservationDate, totalPrice: this.updatedTotalPrice, units: this.unitsForUpdate };
      try {
        const response = await api.put(url, requestBody);
        this.showUpdateReservationDaysModal = false;
        this.updatePrice = false;
        this.updatedReservationDate = null;
        this.updatedUnreservationDate = null;
        this.dailyPriceForUpdate = null;
        this.quantityForUpdate = null;
        this.unitsForUpdate = null;
        swal.fire({ title: `${this.$t('tools.updatedPrice')} ${response.data.totalPrice} Kr.`, icon: 'success', customClass: { confirmButton: 'btn btn-success btn-fill' }, buttonsStyling: false });
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
				this.$toast.error(this.$t('tools.toolReturnError'), { timeout: 0 })
      } finally { this.fetchCostRegistration(); }
    },
    async subReservationCreate() {
      let url = `/Tool/Subreservation`;
      if (this.latestUnreservedEntity === 'ExternalReservation') url = `/Tool/SubReservation/External`;
      const requestBody = { parentReservationId: this.latestUnreservedId, reservedTimestamp: this.updatedReservationDate, unreservedTimestamp: this.updatedUnreservationDate, totalPrice: this.updatedTotalPrice };
      try {
        const response = await api.post(url, requestBody);
        this.showUpdateReservationDaysModal = false;
        this.updatePrice = false;
        this.updatedReservationDate = null;
        this.updatedUnreservationDate = null;
        this.dailyPriceForUpdate = null;
        this.quantityForUpdate = null;
        this.unitsForUpdate = null;
        this.createSubReservation = false;
        this.parentReservationId = null;
        swal.fire({ title: `${this.$t('tools.updatedPrice')} ${response.data.totalPrice} Kr.`, icon: 'success', customClass: { confirmButton: 'btn btn-success btn-fill' }, buttonsStyling: false });
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
				this.$toast.error(this.$t('tools.toolReturnError'), { timeout: 0 });
      } finally { this.fetchCostRegistration(); }
    },
    updateTotalPrice() {
      const startDate = new Date(this.updatedReservationDate);
      const endDate = new Date(this.updatedUnreservationDate);
      if (startDate > endDate) { 
				this.$toast.error(this.$t('errors.startDateBiggerThanEndDate'), { timeout: 0 })
				return;
			}
      if (!endDate || !startDate) { 
				this.updatedTotalPrice = 0;
				return;
			}
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24) + 1);
      this.updatedTotalPrice = this.unitsForUpdate !== null
        ? Math.ceil(this.unitsForUpdate * this.dailyPriceForUpdate * this.quantityForUpdate)
        : Math.ceil(totalDays * this.dailyPriceForUpdate * this.quantityForUpdate);
    },
    isValid() {
      if (!this.updatedReservationDate || !this.updatedUnreservationDate) {
				this.$toast.error(this.$t('errors.datesEmptyOrIncorrect'), { timeout: 0 });
        return false;
      }
      if (!this.isValidDate(this.updatedReservationDate) || !this.isValidDate(this.updatedUnreservationDate)) {
				this.$toast.error(this.$t('errors.datesEmptyOrIncorrect'), { timeout: 0 });
        return false;
      }
      if (new Date(this.updatedReservationDate) > new Date(this.updatedUnreservationDate)) {
        this.$toast.error(this.$t('errors.startDateBiggerThanEndDate'), { timeout: 0 });
        return false;
      }
      if (!Number.isInteger(Number(this.updatedTotalPrice)) || this.updatedTotalPrice === '') {
        this.$toast.error(this.$t('common.needsToBeNumber'), { timeout: 0 });
        return false;
      }
      if (this.isSubReservation || this.createSubReservation) {
        // Validate bounds against selected date range
        if (new Date(this.updatedReservationDate) < new Date(this.fromDate) || new Date(this.updatedUnreservationDate) > new Date(this.toDate)) {
          this.$toast.error(this.$t('errors.subReservationBiggerThanDateRange'), { timeout: 0 });
          return false;
        }
        // Validate bounds against parent reservation
        if (new Date(this.updatedReservationDate) < new Date(this.originalReservationDate) || new Date(this.updatedUnreservationDate) > new Date(this.originalUnreservationDate)) {
          this.$toast.error(this.$t('errors.subReservationBiggerThanParent'), { timeout: 0 });
          return false;
        }
        var projectReservations = this.selectedProject.reservations;
        const newStart = new Date(this.updatedReservationDate);
        const newEnd = new Date(this.updatedUnreservationDate);
        var subReservations = this.latestUnreservedEntity === 'ToolReservation'
          ? projectReservations.filter(r => this.createSubReservation ? r.parentReservationId === this.latestUnreservedId && !r.rented : r.parentReservationId === this.parentReservationId && r.id !== this.latestUnreservedId && !r.rented)
          : projectReservations.filter(r => this.createSubReservation ? r.parentReservationId === this.latestUnreservedId && r.rented : r.parentReservationId === this.parentReservationId && r.id !== this.latestUnreservedId && r.rented);
        // Overlap detection
        for (let sub of subReservations) {
          const linkedSubStart = this.getDayStart(sub.reservedTimestamp);
          const linkedSubEnd = this.getDayStart(sub.unreservedTimestamp);
          const newStartDay = this.getDayStart(newStart);
          const newEndDay = this.getDayStart(newEnd);
          if (newStartDay <= linkedSubEnd && newEndDay >= linkedSubStart) {
            this.$toast.error(this.$t('errors.subReservationsOverlap'), { timeout: 0 });
            return false;
          }
        }
        if (this.isSubReservation) {
          var parentReservation = this.latestUnreservedEntity === 'ToolReservation'
            ? projectReservations.find(r => r.id === this.parentReservationId && !r.rented)
            : projectReservations.find(r => r.id === this.parentReservationId && r.rented);
          const parentStart = new Date(parentReservation.reservedTimestamp.split("T")[0]);
          const parentEnd = new Date(this.toDate);
          if (newStart < parentStart || newEnd > parentEnd) {
            this.$toast.error(this.$t('errors.subReservationBiggerThanParent'), { timeout: 0 });
            return false;
          }
        }
      }
      return true;
    },
    getDayStart(input) {
      const d = new Date(input);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isValidDate(dateString) { const date = new Date(dateString); return date instanceof Date && !isNaN(date); },
    async syncToERP(project) {
      if (this.costSyncWithERPInProgress) return;
      this.costSyncWithERPInProgress = true;
      const reservationIds = project.reservations.filter(r => !r.isExternal).map(r => r.id).join(',');
      const extReservationIds = project.reservations.filter(r => r.isExternal).map(r => r.id).join(',');
      this.erpSyncResult = null;
      // Label per reservation so failures can name the line ("#12 Trappa 1 25.09-28.09")
      this.erpSyncLineLabels = {};
      for (const r of project.reservations) {
        const key = (r.isExternal ? 'e' : 't') + r.id;
        this.erpSyncLineLabels[key] = `#${r.tool.customerToolId} ${r.tool.name} ${this.setupDateRange(r.reservedTimestamp, r.unreservedTimestamp, r.dateRange)}`;
      }
      try {
        const response = await api.post('/CostReport/Sync', { toolReservationIds: reservationIds, externalToolReservationIds: extReservationIds });
        const lines = response.data.costSynchronizationLines || [];
        this.erpSyncResult = {
          created: lines.filter(l => l.costSynchronizationLineStatusId === 1).length,
          updated: lines.filter(l => l.costSynchronizationLineStatusId === 2).length,
          failures: lines
            .filter(l => l.costSynchronizationLineStatusId === 3)
            .map(l => {
              const key = l.externalToolReservationId != null ? 'e' + l.externalToolReservationId : 't' + l.toolReservationId;
              return { label: this.erpSyncLineLabels[key] || '', details: l.details || '' };
            }),
          errorMessage: response.data.errorMessage || null,
        };
        await this.fetchCostRegistration(); // refresh the per-line sync indicators
        this.costSyncWithERPInProgress = false;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.erpSyncResult = { created: 0, updated: 0, failures: [], errorMessage: getApiErrorMessage(this.$t, error) };
        this.costSyncWithERPInProgress = false;
      }
    },
    formatSyncTimestamp(dt) {
      if (!dt) return '';
      const d = new Date(dt);
      const p = (n) => String(n).padStart(2, '0');
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    parseDateFromDMY(dateStr) {
      const [day, month, year] = dateStr.split('/');
      return new Date(+year, +month - 1, +day);
    },
    generatePDF(project) {
      const doc = new jsPDF();
      doc.setFont('times', 'normal');
      const textXPosition = 65;
      const maxTextWidth = doc.internal.pageSize.width - 10;
      const datePeriod = `${this.formatDateDisplay(this.fromDate)} - ${this.formatDateDisplay(this.toDate)}`;
      let yTablePosition = 70;
      doc.setFontSize(12);
      doc.text(datePeriod, maxTextWidth, 20, { align: "right" });
      let currentY = 20;

      const renderPdfContent = () => {
        doc.setFontSize(14);
        doc.text(this.customerInformation.name || '', textXPosition, currentY);
        currentY += 10;
        if (this.customerInformation.identificationNumber) {
          doc.setFontSize(12);
          doc.text(this.customerInformation.identificationNumber, textXPosition, currentY);
          currentY += 10;
        }
        doc.setFontSize(12);
        const projectLine = this.forceProjectConnectionOnRentals && project.projectKey && project.projectKey !== '00000000-0000-0000-0000-000000000000'
          ? `${project.projectName} - ${project.projectKey}` : project.projectName;
        doc.text(projectLine, textXPosition, currentY);
        currentY += 10;

        const activeIdsArray = this.safeActiveIndex === 0 ? this.projectToolIdsToPrint : this.externalReservationIds;
        const selectedReservations = activeIdsArray.length > 0 ? project.reservations.filter(res => activeIdsArray.includes(res.id)) : project.reservations;
        const uniqueUsers = [...new Set(selectedReservations.map(r => r.externalUser).filter(u => u && u.trim() !== ''))];
        uniqueUsers.sort((a, b) => a.length - b.length);
        if (uniqueUsers.length > 0) {
          const userText = uniqueUsers.join(", ");
          const splitText = doc.splitTextToSize(userText, maxTextWidth);
          yTablePosition += (splitText.length - 1) * 10;
          splitText.forEach((line, i) => doc.text(line.replace(/,$/, ''), textXPosition, currentY + i * 10));
        }
        this.generatePdfBody(project, doc, yTablePosition);
      };

      if (this.customerInformation.logoBytes) {
        const logoDataUrl = 'data:image/png;base64,' + this.customerInformation.logoBytes;
        const img = new Image();
        img.src = logoDataUrl;
        img.onload = () => {
          const targetSize = 50;
          const scale = Math.min(targetSize / img.width, targetSize / img.height);
          const width = img.width * scale;
          const height = img.height * scale;
          const xOffset = (targetSize - width) / 2;
          const yOffset = (targetSize - height) / 2;
          doc.addImage(logoDataUrl, 'PNG', 10 + xOffset, 10 + yOffset, width, height);
          renderPdfContent();
        };
        img.onerror = () => {
          console.error("Image failed to load"); // eslint-disable-line no-console
          this.$toast.error(this.$t('errors.companyPhoto'), { timeout: 0 });
        };
      } else {
        renderPdfContent();
      }
    },
    generatePdfBody(project, doc, yPos) {
      doc.setFontSize(10);
      let headers, columnStyles;
      if (this.enableToolPriceTypes) {
        headers = [[{ content: 'Tól', styles: { halign: 'left' } }, { content: 'Eining', styles: { halign: 'center' } }, { content: 'Ein. fjöldi', styles: { halign: 'center' } }, { content: 'Ein. verð', styles: { halign: 'center' } }, { content: 'Verð', styles: { halign: 'right' } }, { content: 'Verð m/vsk', styles: { halign: 'right' } }]];
        columnStyles = { 0: { cellWidth: 40 }, 1: { cellWidth: 16 }, 2: { cellWidth: 16 }, 3: { cellWidth: 34 }, 4: { cellWidth: 40 }, 5: { cellWidth: 40 } };
      } else {
        headers = [[{ content: 'Tól', styles: { halign: 'left' } }, { content: 'Fjöldi', styles: { halign: 'center' } }, { content: 'Einingarverð', styles: { halign: 'center' } }, { content: 'Verð', styles: { halign: 'right' } }, { content: 'Verð m/vsk', styles: { halign: 'right' } }]];
        columnStyles = { 0: { cellWidth: 54 }, 1: { cellWidth: 16 }, 2: { cellWidth: 30 }, 3: { cellWidth: 40 }, 4: { cellWidth: 40 } };
      }
      const body = [], footer = [], colCount = headers[0].length;
      body.push([{ content: '', colSpan: colCount, styles: { cellPadding: 0.1 } }]);

      const groupedByTools = project.reservations.reduce((acc, r) => { if (!acc[r.toolId]) acc[r.toolId] = { tool: r.tool, reservations: [] }; acc[r.toolId].reservations.push(r); return acc; }, {});
      let newProjectTotal = 0, newProjectTotalWithVAT = 0;

      Object.values(groupedByTools).forEach(toolGroup => {
        const toolBody = [];
        let printTool = false;
        toolBody.push([{ content: toolGroup.tool.name, colSpan: colCount, styles: { halign: 'left', fontStyle: 'bold' } }]);
        toolGroup.reservations.forEach(res => {
          const shouldInclude = this.safeActiveIndex !== 0 ? (this.externalReservationIds.length === 0 || this.externalReservationIds.includes(res.id)) : (this.projectToolIdsToPrint.length === 0 || this.projectToolIdsToPrint.includes(res.id));
          if (shouldInclude && res.totalPrice !== 0) {
            printTool = true;
            newProjectTotal += res.totalPrice;
            newProjectTotalWithVAT += res.totalPriceWithVAT;
            const quantity = (res.quantity ?? 1).toString();
            const phasePart = this.enableProjectPhases && res.projectPhaseName ? ` — ${res.projectPhaseName}` : '';
            const dateText = this.setupDateRange(res.reservedTimestamp, res.unreservedTimestamp, res.dateRange) + phasePart;
            const unitPrice = this.formatNumber(res.dailyPrice.toFixed(2));
            const priceCalc = this.formatNumber(res.totalPrice.toFixed(2)) + ' kr';
            const priceWithVAT = this.formatNumber(res.totalPriceWithVAT.toFixed(2)) + ' kr';
            if (this.enableToolPriceTypes) {
              const unitType = this.toolPriceTypeOptions.find(o => o.id === res.toolPriceTypeId)?.type?.toLowerCase() || '';
              let unitsOrDays;
              if (res.units != null) {
                unitsOrDays = res.units.toString();
              } else {
                const fromDate = new Date(this.fromDate); fromDate.setHours(0, 0, 0, 0);
                const toDate = new Date(this.toDate); toDate.setHours(0, 0, 0, 0);
                let start, end;
                if (res.dateRange) {
                  const [startStr, endStr] = res.dateRange.split(' - ');
                  start = this.parseDateFromDMY(startStr.trim());
                  end = this.parseDateFromDMY(endStr.trim());
                } else {
                  start = new Date(res.reservedTimestamp);
                  end = res.unreservedTimestamp ? new Date(res.unreservedTimestamp) : new Date(this.toDate);
                }
                start.setHours(0, 0, 0, 0); end.setHours(0, 0, 0, 0);
                if (start < fromDate) start = new Date(fromDate);
                if (end > toDate) end = new Date(toDate);
                let days = Math.ceil((end - start) / (1000 * 3600 * 24)) + 1;
                unitsOrDays = (days < 1 ? 1 : days).toString();
              }
              const detailCells = [
                { content: unitType, rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: unitsOrDays, rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: unitPrice, rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: priceCalc, rowSpan: 2, styles: { halign: 'right', valign: 'middle' } },
                { content: priceWithVAT, rowSpan: 2, styles: { halign: 'right', valign: 'middle' } }
              ];
              toolBody.push([{ content: `Fjöldi tóla: ${quantity}`, styles: { halign: 'left' } }, ...detailCells]);
              toolBody.push([{ content: dateText, styles: { halign: 'left' } }]);
            } else {
              toolBody.push([{ content: `    ${dateText}`, styles: { halign: 'left' } }, { content: quantity, styles: { halign: 'center' } }, { content: unitPrice, styles: { halign: 'center' } }, { content: priceCalc, styles: { halign: 'right' } }, { content: priceWithVAT, styles: { halign: 'right' } }]);
            }
          }
        });
        if (printTool) { toolBody.push([{ content: '', colSpan: colCount, styles: { cellPadding: 0.1 } }]); body.push(...toolBody); }
      });

      const useFiltered = this.safeActiveIndex === 0 ? this.projectToolIdsToPrint.length > 0 : this.externalReservationIds.length > 0;
      const revNet = useFiltered ? newProjectTotal : project.totalAmount;
      const revVAT = useFiltered ? newProjectTotalWithVAT : project.totalAmountWithVAT;
      footer.push([{ content: 'Heildarupphæð', colSpan: colCount - 2, styles: { halign: 'left', fontStyle: 'bold' } }, { content: this.formatNumber(revNet.toFixed(2)) + ' kr', styles: { halign: 'right' } }, { content: this.formatNumber(revVAT.toFixed(2)) + ' kr', styles: { halign: 'right' } }]);

      autoTable(doc, { head: headers, body, foot: footer, startY: yPos, theme: 'plain', styles: { cellPadding: 1, fontSize: 10 }, headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: 'bold', lineWidth: { top: 0.2, bottom: 0.2 }, lineColor: [0, 0, 0] }, bodyStyles: { textColor: 0 }, footStyles: { fillColor: [255, 255, 255], lineWidth: { top: 0.2 }, lineColor: [0, 0, 0] }, showHead: 'firstPage', showFoot: 'lastPage', columnStyles });
      doc.save(`${project.projectName}.pdf`);
    }
  }
};
</script>

<style scoped>
.calendar { padding-bottom: 15px; }
.calendar :deep(.el-date-editor.el-input) { width: 100%; }
@media (max-width: 768px) {
  .calendar {
    flex-direction: column;
    align-items: stretch !important;
  }
  .calendar > span.mx-2 { display: none; }
}
.download-button { width: 100%; }
.actions-container { display: flex; align-items: center; float: right; gap: 8px; }
.action-container { display: flex; align-items: center; gap: 8px; }
.nav-item { padding-left: 0.5rem; padding-right: 0.5rem; }
.search-input { width: 100%; }
.project-header h5 { margin-bottom: 0.5rem; }
.text-info { color: #17a2b8 !important; }
.text-warning { color: #ffc107 !important; }
.text-danger { color: #dc3545 !important; }
.project-close-btn { font-size: 28px; cursor: pointer; color: var(--primary-color); line-height: 1; }
</style>
